import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import DocumentViewerModal from "./DocumentViewerModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import "./RecruitDetailView.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

const RecruitDetailView = forwardRef(
  ({ recruit, onBack, onSendOffer, scheduledMeeting }, ref) => {
    const { getVerifiedJWT } = useAuth();
    const { showSuccess, showError } = useToast();
    const [activeTab, setActiveTab] = useState("History");
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(true);
    const [viewerDocument, setViewerDocument] = useState(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [showMenuDropdown, setShowMenuDropdown] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedRecruit, setEditedRecruit] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const dropdownRef = useRef(null);

    const tabConfig = ["History", "Offer"];

    useEffect(() => {
      if (recruit?.id) {
        fetchHistory();
        fetchDocuments();
        // Reset edit mode when recruit changes
        setIsEditMode(false);
        setEditedRecruit({
          first_name: recruit.first_name || "",
          last_name: recruit.last_name || "",
          phone_number: recruit.phone_number || recruit.phone || "",
          email: recruit.email || "",
          instagram_username: recruit.instagram_username || "",
          linkedin_profile: recruit.linkedin_profile || "",
        });
      }

      // Set up auto-refresh every 10 seconds to catch new documents and status updates
      const refreshInterval = setInterval(() => {
        if (recruit?.id) {
          fetchHistory();
          fetchDocuments();
        }
      }, 10000);

      return () => {
        clearInterval(refreshInterval);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recruit?.id]);

    // Handle clicks outside dropdown
    useEffect(() => {
      const handleClickOutside = event => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setShowMenuDropdown(false);
        }
      };

      if (showMenuDropdown) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showMenuDropdown]);

    // Expose fetchHistory method to parent component
    useImperativeHandle(ref, () => ({
      refreshHistory: () => {
        fetchHistory();
      },
    }));

    const fetchHistory = async () => {
      setLoadingHistory(true);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const response = await fetch(
          getApiEndpoint(`/v1/recruiting/leads/${recruit.id}/history`),
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const events = data.data || [];

          // Add "Lead Created" event at the end (oldest)
          const leadCreatedEvent = {
            id: "created",
            event_type: "Created",
            event_date: recruit.created_at,
            notes: `${recruit.name} was added to the system`,
            created_at: recruit.created_at,
          };

          setHistory([...events, leadCreatedEvent]);
        }
      } catch (error) {
      } finally {
        setLoadingHistory(false);
      }
    };

    const fetchDocuments = async () => {
      setLoadingDocuments(true);
      try {
        const jwt = await getVerifiedJWT();

        if (!jwt) {
          return;
        }

        const endpoint = getApiEndpoint(
          `/v1/recruiting/leads/${recruit.id}/documents`
        );

        // Fetch documents for this specific recruit
        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const recruitDocs = data.data || [];

          setDocuments(recruitDocs);
        }
      } catch (error) {
      } finally {
        setLoadingDocuments(false);
      }
    };

    const handleDownloadDocument = async (documentId, documentTitle) => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const response = await fetch(
          getApiEndpoint(`/v1/esignature/documents/${documentId}/download`),
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // The response contains the presigned URL
          if (data.path) {
            // Open the presigned URL in a new tab to download
            window.open(data.path, "_blank");
          }
        }
      } catch (error) {}
    };

    const formatEventDate = dateString => {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatEventTime = dateString => {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    };

    const getEventIcon = event => {
      if (event.event_type === "Contact") {
        return "phone";
      } else if (event.event_type === "Meeting") {
        return "event";
      } else if (event.event_type === "Meeting Scheduled") {
        return "event_available";
      } else if (event.event_type === "Meeting Cancelled") {
        return "event_busy";
      } else if (event.event_type === "Created") {
        return "person_add";
      } else if (event.event_type === "Proposal Sent") {
        return "send";
      } else if (event.event_type === "Proposal Viewed") {
        return "visibility";
      } else if (event.event_type === "Proposal Signed") {
        return "draw";
      } else if (event.event_type === "Proposal Completed") {
        return "check_circle";
      }
      return "info";
    };

    const getEventTitle = event => {
      if (event.event_type === "Contact") {
        return `Contact via ${event.contact_method || "Phone"}`;
      } else if (event.event_type === "Meeting") {
        if (event.meeting_occurred) {
          return event.has_offer_proposal
            ? "Meeting (with Offer Proposal)"
            : "Meeting";
        }
        return "Meeting (Did Not Occur)";
      } else if (event.event_type === "Meeting Scheduled") {
        return "Meeting Scheduled";
      } else if (event.event_type === "Meeting Cancelled") {
        return "Meeting Cancelled";
      } else if (event.event_type === "Created") {
        return "Lead Created";
      } else if (event.event_type === "Proposal Sent") {
        return "Offer Proposal Sent";
      } else if (event.event_type === "Proposal Viewed") {
        return "Proposal Viewed by Recipient";
      } else if (event.event_type === "Proposal Signed") {
        return "Proposal Signed";
      } else if (event.event_type === "Proposal Completed") {
        return "All Signatures Completed";
      }
      return event.event_type;
    };

    const handleDeleteRecruit = () => {
      setShowMenuDropdown(false);
      setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
      setShowDeleteConfirm(false);

      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) {
          handleAuthError("Invalid jwt");
          return;
        }

        const response = await fetch(
          getApiEndpoint(`/v2/recruiting/leads/${recruit.id}`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          showSuccess(`${recruit.name} has been deleted successfully`);
          onBack(); // Navigate back to the recruiting dashboard
        } else {
          showError("Failed to delete recruit");
        }
      } catch (error) {
        handleGenericError(error, "Failed to delete recruit");
      }
    };

    const handleEditClick = () => {
      setIsEditMode(true);
      setShowMenuDropdown(false);
    };

    const handleCancelEdit = () => {
      setIsEditMode(false);
      // Reset to original values
      setEditedRecruit({
        first_name: recruit.first_name || "",
        last_name: recruit.last_name || "",
        phone_number: recruit.phone_number || recruit.phone || "",
        email: recruit.email || "",
        instagram_username: recruit.instagram_username || "",
        linkedin_profile: recruit.linkedin_profile || "",
      });
    };

    const handleSaveEdit = async () => {
      setIsSaving(true);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) {
          handleAuthError("Invalid jwt");
          return;
        }

        const response = await fetch(
          getApiEndpoint(`/v1/recruiting/leads/${recruit.id}`),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${jwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(editedRecruit),
          }
        );

        if (response.ok) {
          const data = await response.json();
          showSuccess("Recruit information updated successfully");
          setIsEditMode(false);
          // Update the recruit object with new data
          Object.assign(recruit, {
            first_name: data.data.first_name,
            last_name: data.data.last_name,
            name: `${data.data.first_name} ${data.data.last_name}`,
            phone_number: data.data.phone_number,
            phone: data.data.phone_number,
            email: data.data.email,
            instagram_username: data.data.instagram_username,
            linkedin_profile: data.data.linkedin_profile,
          });
        } else {
          const errorData = await response.json();
          showError(
            errorData.message || "Failed to update recruit information"
          );
        }
      } catch (error) {
        handleGenericError(error, "Failed to update recruit information");
      } finally {
        setIsSaving(false);
      }
    };

    const handleInputChange = (field, value) => {
      setEditedRecruit(prev => ({
        ...prev,
        [field]: value,
      }));
    };

    return (
      <div className="recruit-detail-view">
        {/* Header */}
        <div className="recruit-detail-header">
          <button className="recruit-detail-back-btn" onClick={onBack}>
            <span className="material-icons">arrow_back</span>
            <span>Back to Recruiting</span>
          </button>
        </div>

        {/* Contact Info Card */}
        <div className="recruit-contact-card">
          <div className="recruit-contact-left">
            <div className="recruit-contact-name-section">
              {isEditMode ? (
                <div className="recruit-edit-name-fields">
                  <input
                    type="text"
                    className="recruit-edit-input"
                    placeholder="First Name"
                    value={editedRecruit.first_name}
                    onChange={e =>
                      handleInputChange("first_name", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="recruit-edit-input"
                    placeholder="Last Name"
                    value={editedRecruit.last_name}
                    onChange={e =>
                      handleInputChange("last_name", e.target.value)
                    }
                  />
                </div>
              ) : (
                <>
                  <h1 className="recruit-contact-name">{recruit.name}</h1>
                  <span className="recruit-status-badge new-recruit">
                    New Recruit
                  </span>
                </>
              )}
            </div>
            <div className="recruit-contact-details">
              <div className="recruit-contact-item">
                <span className="material-icons">phone</span>
                {isEditMode ? (
                  <input
                    type="tel"
                    className="recruit-edit-input-inline"
                    placeholder="Phone Number"
                    value={editedRecruit.phone_number}
                    onChange={e =>
                      handleInputChange("phone_number", e.target.value)
                    }
                  />
                ) : (
                  <span>{recruit.phone_number || recruit.phone || "N/A"}</span>
                )}
              </div>
              <div className="recruit-contact-item">
                <span className="material-icons">email</span>
                {isEditMode ? (
                  <input
                    type="email"
                    className="recruit-edit-input-inline"
                    placeholder="Email"
                    value={editedRecruit.email}
                    onChange={e => handleInputChange("email", e.target.value)}
                  />
                ) : (
                  <span>{recruit.email || "N/A"}</span>
                )}
              </div>
              <div className="recruit-contact-item">
                <span className="material-icons">camera_alt</span>
                {isEditMode ? (
                  <input
                    type="text"
                    className="recruit-edit-input-inline"
                    placeholder="Instagram Username"
                    value={editedRecruit.instagram_username}
                    onChange={e =>
                      handleInputChange("instagram_username", e.target.value)
                    }
                  />
                ) : (
                  <span>{recruit.instagram_username || "N/A"}</span>
                )}
              </div>
              <div className="recruit-contact-item">
                <span className="material-icons">work</span>
                {isEditMode ? (
                  <input
                    type="text"
                    className="recruit-edit-input-inline"
                    placeholder="LinkedIn Profile"
                    value={editedRecruit.linkedin_profile}
                    onChange={e =>
                      handleInputChange("linkedin_profile", e.target.value)
                    }
                  />
                ) : (
                  <span>{recruit.linkedin_profile || "N/A"}</span>
                )}
              </div>
              <div className="recruit-contact-item">
                <span>Manager:</span>
                <span className="recruit-manager-name">
                  {recruit.recruiter_name || "N/A"}
                </span>
              </div>
            </div>
          </div>
          <div className="recruit-contact-right">
            {isEditMode ? (
              <div className="recruit-edit-actions">
                <button
                  className="btn btn-ghost-secondary"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            ) : (
              <>
                <button
                  className="btn btn-primary btn-send-offer"
                  onClick={onSendOffer}
                >
                  Send Offer
                </button>
                <div className="recruit-menu-wrapper" ref={dropdownRef}>
                  <button
                    className="btn btn-ghost-secondary btn-icon-only"
                    onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                  >
                    <span className="material-icons">more_vert</span>
                  </button>
                  {showMenuDropdown && (
                    <div className="recruit-dropdown-menu">
                      <button
                        className="recruit-dropdown-item"
                        onClick={handleEditClick}
                      >
                        <span className="material-icons">edit</span>
                        <span>Edit Information</span>
                      </button>
                      <button
                        className="recruit-dropdown-item recruit-dropdown-item-danger"
                        onClick={handleDeleteRecruit}
                      >
                        <span className="material-icons">delete</span>
                        <span>Delete Recruit</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="recruit-detail-tabs">
          {tabConfig.map(tab => (
            <button
              key={tab}
              className={`recruit-detail-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="recruit-detail-content">
          {activeTab === "History" && (
            <div className="recruit-tab-panel">
              {loadingHistory ? (
                <div className="loading-state">
                  <p>Loading history...</p>
                </div>
              ) : history.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <span className="material-icons">history</span>
                  </div>
                  <h3>No activity yet</h3>
                  <p>Contact and meeting history will appear here</p>
                </div>
              ) : (
                <div className="history-timeline">
                  {history.map(event => (
                    <div
                      key={event.id}
                      className={`history-event ${event.event_type.toLowerCase()}-event ${
                        event.event_type === "Contact" && !event.was_successful
                          ? "unsuccessful"
                          : ""
                      }`}
                    >
                      <div className="event-icon-wrapper">
                        <div className="event-icon">
                          <span className="material-icons">
                            {getEventIcon(event)}
                          </span>
                        </div>
                        <div className="event-line"></div>
                      </div>
                      <div className="event-content">
                        <div className="event-header">
                          <h4 className="event-title">
                            {getEventTitle(event)}
                          </h4>
                          <div className="event-meta">
                            <span className="event-date">
                              {formatEventDate(
                                event.event_date ||
                                  event.created_at ||
                                  event.created
                              )}
                            </span>
                            <span className="event-time">
                              {formatEventTime(
                                event.event_date ||
                                  event.created_at ||
                                  event.created
                              )}
                            </span>
                          </div>
                        </div>
                        {event.event_type === "Contact" && (
                          <div className="event-badges">
                            <span
                              className={`event-badge ${
                                event.was_successful
                                  ? "badge-success"
                                  : "badge-warning"
                              }`}
                            >
                              {event.was_successful
                                ? "Successful"
                                : "Unsuccessful"}
                            </span>
                            <span className="event-badge badge-method">
                              {event.contact_method}
                            </span>
                          </div>
                        )}
                        {event.event_type === "Meeting" && (
                          <div className="event-badges">
                            <span
                              className={`event-badge ${
                                event.meeting_occurred
                                  ? "badge-success"
                                  : "badge-warning"
                              }`}
                            >
                              {event.meeting_occurred
                                ? "Occurred"
                                : "Did Not Occur"}
                            </span>
                            {event.has_offer_proposal && (
                              <span className="event-badge badge-highlight">
                                Offer Proposed
                              </span>
                            )}
                          </div>
                        )}
                        {(event.event_type === "Proposal Sent" ||
                          event.event_type === "Proposal Viewed" ||
                          event.event_type === "Proposal Signed" ||
                          event.event_type === "Proposal Completed") && (
                          <div className="event-badges">
                            <span className="event-badge badge-primary">
                              {event.document_title || "Offer Document"}
                            </span>
                            {event.recipient_name && (
                              <span className="event-badge badge-info">
                                {event.recipient_name}
                              </span>
                            )}
                          </div>
                        )}
                        {event.notes && (
                          <div className="event-notes">
                            <p>{event.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "Offer" && (
            <div className="recruit-tab-panel">
              {loadingDocuments ? (
                <div className="loading-state">
                  <p>Loading documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <span className="material-icons">description</span>
                  </div>
                  <h3>No documents sent</h3>
                  <p>Send offer documents to this recruit</p>
                  <button className="btn btn-primary" onClick={onSendOffer}>
                    Send Offer
                  </button>
                </div>
              ) : (
                <div className="documents-list">
                  <div className="documents-header">
                    <h3>Offer Documents</h3>
                    <button className="btn btn-primary" onClick={onSendOffer}>
                      <span
                        className="material-icons"
                        style={{ fontSize: "1.2rem" }}
                      >
                        add
                      </span>
                      <span>Send New Document</span>
                    </button>
                  </div>
                  <div className="documents-grid">
                    {documents.map(doc => (
                      <div
                        key={doc.id}
                        className="document-card clickable"
                        onClick={() => {
                          setViewerDocument(doc);
                          setIsViewerOpen(true);
                        }}
                      >
                        <div className="document-icon">
                          <span className="material-icons">description</span>
                        </div>
                        <div className="document-content">
                          <h4 className="document-title">{doc.title}</h4>
                          {doc.description && (
                            <p className="document-description">
                              {doc.description}
                            </p>
                          )}
                          <div className="document-meta">
                            <span
                              className={`document-status status-${doc.status}`}
                            >
                              {doc.status === "completed"
                                ? "✓ Signed"
                                : doc.status}
                            </span>
                            {doc.sent_at && (
                              <span className="document-date">
                                Sent: {formatEventDate(doc.sent_at)}
                              </span>
                            )}
                            {doc.completed_at && (
                              <span className="document-date">
                                Completed: {formatEventDate(doc.completed_at)}
                              </span>
                            )}
                          </div>
                          {doc.status === "completed" &&
                            doc.signed_storage_path && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDownloadDocument(doc.id, doc.title);
                                }}
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  marginTop: "0.75rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                }}
                              >
                                <span
                                  className="material-icons"
                                  style={{ fontSize: "1rem" }}
                                >
                                  download
                                </span>
                                <span>Download Signed</span>
                              </button>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Document Viewer Modal */}
        <DocumentViewerModal
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setViewerDocument(null);
          }}
          document={viewerDocument}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          recruitName={recruit?.name || ""}
        />
      </div>
    );
  }
);

export default RecruitDetailView;
